import {
    Box,
    Center,
    Divider,
    HStack,
    Image,
    Text,
    useToast,
} from "@chakra-ui/react";
import CompanyInfo from "components/CompanyInfo";
import OrderItem from "routes/Order/components/OrderItem";
import SubTitle from "routes/Order/components/SubTitle";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    ItemModel,
    PaymentListModel,
    PaymentModel,
    ShippingAddressModel,
} from "lib/api/model";
import OrderStepStatusBar from "./components/OrderStepStatusBar";
import { useQuery } from "@tanstack/react-query";
import { OrderStatus, ResStatus } from "lib/api/enum";
import { callOrderDetailApi } from "./api";
import { convertDateFullTime } from "lib/utils/regular";
import { digit } from "lib/utils/digit";
import ButlerInfo from "./components/ButlerInfo";
import ParcelInfo from "./components/ParcelInfo";
import withAuth from "lib/hook/withAuth";

const OrderDetail = () => {
    const params = useParams();
    const toast = useToast();

    const [orderId, setOrderId] = useState<number>();
    const [merchantUid, setMerchantuid] = useState<string>("");
    const [orderStatus, setOrderStatus] = useState<number>(0);
    const [deliveryMethod, setDeliveryMethod] = useState<number>(1);
    const [regDate, setRegDate] = useState<number>();

    // 주문 아이템
    const [itemsData, setItemsData] = useState<ItemModel[]>([]);
    const [pickupData, setPickupData] = useState<ShippingAddressModel | null>();
    const [deliveryData, setDeliveryData] =
        useState<ShippingAddressModel | null>();
    const [paymentData, setPaymentData] = useState<PaymentModel>();
    const [paymentListData, setPaymentListData] =
        useState<PaymentListModel[]>();

    useQuery(
        ["callOrderDetailApi"],
        () => {
            return callOrderDetailApi({
                order_id: params.orderid ? Number.parseInt(params.orderid) : 0,
            });
        },
        {
            onSuccess: (data) => {
                console.log("detail : ", data);
                if (data.status === ResStatus.RES_SUCCESS) {
                    setOrderId(data.command.order_id);
                    setMerchantuid(data.command.merchant_uid);
                    setOrderStatus(data.command.order_status);
                    setDeliveryMethod(data.command.delivery_method);
                    setRegDate(data.command.reg_date);
                    setItemsData(data.command.item_list);
                    setPickupData(data.command.pickup);
                    setDeliveryData(data.command.delivery);
                    setPaymentData(data.command.payment);
                    setPaymentListData(data.command.payment_list);
                } else {
                    toast({
                        title: "오류",
                        description: data.result_message,
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                        position: "top",
                    });
                }
            },
            onError: (err: any) => {
                toast({
                    title: "오류",
                    description: err.message,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "top",
                });
            },
        }
    );

    return (
        <Box>
            <Center
                w="100%"
                h="4rem"
                bgColor={"white"}
                fontSize="1.25rem"
                fontWeight={"semibold"}
                zIndex="1"
            >
                주문서
            </Center>
            <Box mx="20px">
                <SubTitle mb="0.625rem">진행상태</SubTitle>
                <OrderStepStatusBar
                    parcel={deliveryMethod === 1 || deliveryMethod === 3}
                    stepStatus={orderStatus}
                />
            </Box>

            <Box bgColor={"#f5f6f8"} h={"0.5rem"} my="1.25rem" />

            <Box mx="20px">
                <SubTitle>주문정보</SubTitle>
                <HStack mb="0.5rem">
                    <Text
                        fontSize="0.813rem"
                        fontWeight={"medium"}
                        color="#616161"
                        mr="2.8125rem"
                    >
                        주문일시
                    </Text>
                    <Text fontSize="0.813rem" fontWeight={"medium"} flex="1">
                        {convertDateFullTime(regDate)}
                    </Text>
                </HStack>
                <HStack mb="0.5rem">
                    <Text
                        fontSize="0.813rem"
                        fontWeight={"medium"}
                        color="#616161"
                        mr="2.8125rem"
                    >
                        주문번호
                    </Text>
                    <Text fontSize="0.813rem" fontWeight={"medium"} flex="1">
                        {orderId}
                    </Text>
                </HStack>
            </Box>

            <Box bgColor={"#f5f6f8"} h={"0.5rem"} my="1.25rem" />

            <Box mx="20px">
                <SubTitle>주문내역</SubTitle>
                {itemsData.map((data) => (
                    <OrderItem key={`order_item_${data.item_id}`} data={data} />
                ))}
            </Box>

            {!(
                orderStatus === OrderStatus.CANCEL_ORDER ||
                orderStatus === OrderStatus.REJECT_ORDER
            ) && (
                <>
                    <Box bgColor={"#f5f6f8"} h={"0.5rem"} my="1.25rem" />
                    <Box mx="20px">
                        {deliveryMethod === 0 ? (
                            <ButlerInfo
                                orderStatus={orderStatus}
                                butlerAddressData={
                                    orderStatus < OrderStatus.MINT_COMPLETE
                                        ? pickupData
                                        : deliveryData
                                }
                            />
                        ) : (
                            <ParcelInfo
                                orderStatus={orderStatus}
                                parcelAddressData={
                                    orderStatus < OrderStatus.MINT_COMPLETE
                                        ? pickupData
                                        : deliveryData
                                }
                            />
                        )}
                    </Box>
                </>
            )}
            <Box bgColor={"#f5f6f8"} h={"0.5rem"} my="1.25rem" />

            <Box mx="20px" mb="3.75rem">
                <SubTitle>결제정보</SubTitle>
                <HStack
                    fontSize="0.875rem"
                    fontWeight={"medium"}
                    mb="0.313rem"
                    justifyContent={"space-between"}
                >
                    <Text color="#757575">서비스 금액</Text>
                    <Text color="#212121">
                        {paymentData && digit(paymentData?.order_amount!)} 원
                    </Text>
                </HStack>
                <HStack
                    fontSize="0.875rem"
                    fontWeight={"medium"}
                    mb="0.313rem"
                    justifyContent={"space-between"}
                >
                    <Text color="#757575">배송비</Text>
                    <Text color="#212121">
                        {paymentData && digit(paymentData?.delivery_fee!)} 원
                    </Text>
                </HStack>
                <HStack
                    fontSize="0.875rem"
                    fontWeight={"medium"}
                    mb="0.313rem"
                    justifyContent={"space-between"}
                >
                    <Text color="#757575">할인 금액</Text>
                    <Text color="#212121">
                        {paymentData && digit(paymentData?.discount_amount!)} 원
                    </Text>
                </HStack>
                <HStack
                    fontSize="0.875rem"
                    fontWeight={"medium"}
                    mb="0.313rem"
                    justifyContent={"space-between"}
                >
                    <Text color="#757575">취소 금액</Text>
                    <Text color="#212121">
                        {paymentData &&
                            `${
                                paymentData?.cancel_amount! === 0 ? "" : "-"
                            }${digit(paymentData?.cancel_amount!)}`}{" "}
                        원
                    </Text>
                </HStack>
                <Divider my="0.625rem" />
                <SubTitle>결제수단</SubTitle>
                {paymentListData?.map((pay, index) => (
                    <HStack
                        key={`paytype_${index}`}
                        fontSize="0.875rem"
                        fontWeight={"medium"}
                        mb="0.5rem"
                        justifyContent={"space-between"}
                    >
                        <Text color="#757575">{pay.payment_method_name}</Text>
                        <Text color="#212121">
                            {digit(pay.payment_amount)} 원
                        </Text>
                    </HStack>
                ))}
                <HStack
                    fontSize="1rem"
                    fontWeight={"semibold"}
                    justifyContent={"space-between"}
                >
                    <Text>총 결제 금액</Text>
                    <Text>
                        {paymentData && digit(paymentData?.total_amount!)} 원
                    </Text>
                </HStack>
            </Box>
            <CompanyInfo />
            <Box
                w={"68px"}
                h={"68px"}
                position={"fixed"}
                bottom={"20px"}
                right={"16px"}
                zIndex={100}
                onClick={() =>
                    window.open("http://pf.kakao.com/_Qxjcxob/chat", "_blank")
                }
            >
                <Image src="/assets/images/kakao_68x68.svg" />
            </Box>
        </Box>
    );
};

export default withAuth(OrderDetail);
